import { useRef, useState } from 'react';
import { useAssets, addAssetFile, removeAsset, setAssetPreload } from './assetStore';
import './AssetsPanel.css';

/** Sidebar panel for the virtual /assets folder: upload, preview, remove. */
export function AssetsPanel() {
  const assets = useAssets();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  async function onFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    for (const file of Array.from(files)) {
      try {
        await addAssetFile(file);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed');
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="assets-panel" data-testid="assets-panel">
      <div className="assets-toolbar">
        <button
          className="assets-upload-btn"
          data-testid="asset-upload-btn"
          onClick={() => inputRef.current?.click()}
        >
          Upload asset
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/json,audio/mpeg,audio/ogg,audio/wav,audio/webm"
          data-testid="asset-upload-input"
          style={{ display: 'none' }}
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>
      {error && <div className="assets-error" data-testid="asset-error">{error}</div>}
      {assets.length === 0 ? (
        <div className="assets-empty">No assets yet. Upload images, JSON, or audio — read them in code via <code>AssetManager.getImage('…')</code>.</div>
      ) : (
        <ul className="assets-list">
          {assets.map((a) => (
            <li key={a.path} className="assets-item" data-testid={`asset-item-${a.path}`}>
              {a.folder === 'images' ? (
                <img className="assets-thumb" src={`data:${a.mimeType};base64,${a.base64}`} alt={a.name} />
              ) : (
                <span className="assets-thumb assets-thumb--file">{a.mimeType.split('/')[1] ?? 'file'}</span>
              )}
              <span className="assets-path" title={a.path}>{a.path}</span>
              <span className="assets-size">{(a.size / 1024).toFixed(1)} KB</span>
              <input
                type="checkbox"
                className="assets-preload"
                data-testid={`asset-preload-${a.path}`}
                checked={a.preload !== false}
                onChange={(e) => setAssetPreload(a.path, e.target.checked)}
                title="Preload before systems start"
              />
              <button
                className="assets-remove"
                data-testid={`asset-remove-${a.path}`}
                onClick={() => removeAsset(a.path)}
                title="Remove"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
